/**
 * Responsive image variant pipeline (client-side).
 *
 * Uploaded photos are decoded once, validated against dimension/size limits,
 * auto-downscaled, then re-encoded into a ladder of responsive widths in both
 * AVIF (when the browser can encode it) and WebP. The resulting variant list is
 * stored alongside the record so the UI can serve the smallest suitable file.
 */

export type ImageVariant = {
  url: string;
  width: number;
  type: string;
  bytes?: number;
};

/** Thumbnail rung — used by gallery grids and list cards. */
export const THUMB_WIDTH = 240;
/** Widths we generate. Larger ladder rungs are skipped for small sources. */
export const VARIANT_WIDTHS = [THUMB_WIDTH, 480, 960, 1600] as const;
/** Hard ceiling for anything stored — larger uploads are auto-downscaled. */
export const MAX_STORED_DIMENSION = 2000;
/** Uploads beyond this are rejected outright (before any decode work). */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
/** Sources beyond this are rejected — almost certainly not a photo for the web. */
export const MAX_SOURCE_DIMENSION = 12000;

const AVIF_QUALITY = 0.5;
const WEBP_QUALITY = 0.8;

const PASSTHROUGH = /^image\/(gif|svg\+xml)$/i;

let avifPromise: Promise<boolean> | null = null;

async function canEncodeAvif(): Promise<boolean> {
  if (!avifPromise) {
    avifPromise = (async () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 8;
        canvas.height = 8;
        const blob: Blob | null = await new Promise((r) => canvas.toBlob(r, "image/avif", 0.6));
        return !!blob && blob.type === "image/avif";
      } catch {
        return false;
      }
    })();
  }
  return avifPromise;
}

async function decode(src: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(src);
    } catch {
      /* fall back to <img> */
    }
  }
  const url = URL.createObjectURL(src);
  try {
    const img = new Image();
    img.decoding = "async";
    img.crossOrigin = "anonymous";
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Could not read image"));
      img.src = url;
    });
    return img;
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

function drawTo(source: CanvasImageSource, w: number, h: number): HTMLCanvasElement | null {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, w, h);
  return canvas;
}

function encode(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

export type BuiltVariant = { file: File; width: number; type: string };

/** Conversion metadata recorded for every optimized upload. */
export type ConversionMeta = {
  original_format: string;
  original_bytes: number;
  source_width: number;
  source_height: number;
  output_formats: string[];
  widths: number[];
  target_width: number;
  target_height: number;
  thumb_width: number | null;
  quality: { avif: number; webp: number };
  optimized_bytes: number;
  file_count: number;
  converted_at: string;
};

export type BuiltImageSet = {
  /** Largest optimized rendition — becomes the stored `media_url`. */
  primary: BuiltVariant;
  /** Every rendition (including the primary), smallest first. */
  variants: BuiltVariant[];
  originalBytes: number;
  sourceWidth: number;
  sourceHeight: number;
  /** Conversion details for the admin media view. */
  meta: ConversionMeta;
};

/**
 * Validates an image before any storage work happens.
 * Throws for files that are too large; oversized *dimensions* are downscaled
 * automatically by `buildImageSet`, not rejected.
 */
export function validateUpload(file: File): void {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`File too large (${formatBytes(file.size)}). Maximum is ${formatBytes(MAX_UPLOAD_BYTES)}.`);
  }
}

/**
 * Builds the responsive variant ladder for an image file.
 * Returns `null` when the file isn't a re-encodable raster image (GIF/SVG/video).
 */
export async function buildImageSet(
  blob: Blob,
  baseName: string,
  opts: { maxDimension?: number } = {},
): Promise<BuiltImageSet | null> {
  const type = (blob.type || "").toLowerCase();
  if (!type.startsWith("image/") || PASSTHROUGH.test(type)) return null;
  if (typeof document === "undefined") return null;

  const maxDim = opts.maxDimension ?? MAX_STORED_DIMENSION;
  const source = await decode(blob);
  const sw = "width" in source ? source.width : 0;
  const sh = "height" in source ? source.height : 0;
  if (!sw || !sh) return null;
  if (Math.max(sw, sh) > MAX_SOURCE_DIMENSION) {
    throw new Error(`Image is ${sw}×${sh}px — too large to process (max ${MAX_SOURCE_DIMENSION}px on the long edge).`);
  }

  const useAvif = await canEncodeAvif();
  const formats: Array<{ type: string; quality: number; ext: string }> = [
    ...(useAvif ? [{ type: "image/avif", quality: AVIF_QUALITY, ext: "avif" }] : []),
    { type: "image/webp", quality: WEBP_QUALITY, ext: "webp" },
  ];

  // Cap by the smaller of the source width and the stored ceiling.
  const scaleCap = Math.min(1, maxDim / Math.max(sw, sh));
  const cappedWidth = Math.max(1, Math.round(sw * scaleCap));
  const ladder = Array.from(
    new Set([...VARIANT_WIDTHS.filter((w) => w < cappedWidth), cappedWidth]),
  ).sort((a, b) => a - b);

  const stem = baseName.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9._-]/g, "_").slice(-60) || "image";
  const variants: BuiltVariant[] = [];

  for (const width of ladder) {
    const height = Math.max(1, Math.round((width / sw) * sh));
    const canvas = drawTo(source as CanvasImageSource, width, height);
    if (!canvas) continue;
    for (const fmt of formats) {
      const out = await encode(canvas, fmt.type, fmt.quality);
      if (!out || !out.type.startsWith("image/")) continue;
      variants.push({
        file: new File([out], `${stem}-${width}.${fmt.ext}`, { type: out.type, lastModified: Date.now() }),
        width,
        type: out.type,
      });
    }
  }

  if ("close" in source && typeof source.close === "function") source.close();
  if (variants.length === 0) return null;

  const maxWidth = Math.max(...variants.map((v) => v.width));
  const atMax = variants.filter((v) => v.width === maxWidth);
  // Prefer WebP for the canonical URL: universally supported by browsers,
  // email clients and social crawlers. AVIF is served via <picture>.
  const primary = atMax.find((v) => v.type === "image/webp") ?? atMax[0]!;

  const widths = [...new Set(variants.map((v) => v.width))].sort((a, b) => a - b);
  const optimizedBytes = variants.reduce((n, v) => n + v.file.size, 0);

  return {
    primary,
    variants,
    originalBytes: blob.size,
    sourceWidth: sw,
    sourceHeight: sh,
    meta: {
      original_format: type || "unknown",
      original_bytes: blob.size,
      source_width: sw,
      source_height: sh,
      output_formats: [...new Set(variants.map((v) => v.type))],
      widths,
      target_width: maxWidth,
      target_height: Math.max(1, Math.round((maxWidth / sw) * sh)),
      thumb_width: widths.includes(THUMB_WIDTH) ? THUMB_WIDTH : (widths[0] ?? null),
      quality: { avif: AVIF_QUALITY, webp: WEBP_QUALITY },
      optimized_bytes: optimizedBytes,
      file_count: variants.length,
      converted_at: new Date().toISOString(),
    },
  };
}

export function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

/** Total bytes of a built set (all formats/widths combined). */
export function setBytes(set: BuiltImageSet): number {
  return set.variants.reduce((n, v) => n + v.file.size, 0);
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result as string;
      const idx = res.indexOf(",");
      resolve(idx >= 0 ? res.slice(idx + 1) : res);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Narrow unknown JSON into a usable variant list. */
export function parseVariants(raw: unknown): ImageVariant[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((v: any) => v && typeof v.url === "string" && typeof v.width === "number" && typeof v.type === "string")
    .map((v: any) => ({ url: v.url as string, width: v.width as number, type: v.type as string, bytes: v.bytes }))
    .sort((a, b) => a.width - b.width);
}
